/**
 * Real-time streaming transcription via WebSocket.
 * Connects to the backend's /companion/transcribe-stream endpoint,
 * which proxies to Deepgram.
 *
 * Audio chunks are sent as binary messages.
 * Transcript updates arrive as JSON: { type: "partial"|"final", text: "...",
 * language?: "...", confidence?: 0-1 }
 */
export interface TranscriptionResult {
  text: string
  /** Detected language; defaults to 'en' when the server reported none. */
  language: string
  /** Language only when the server actually reported one (the gate needs the difference). */
  detectedLanguage?: string
  /** Length-weighted STT confidence (0-1); absent when the provider sends none. */
  confidence?: number
}

export interface TranscriptionClientOptions {
  /**
   * Speech-to-text hints (tickers on screen, trading terms), read at connect
   * time and sent as repeated `keyterm` query params. The server re-validates
   * and caps them.
   */
  getKeyterms?: () => string[]
}

export class TranscriptionClient {
  private wsUrl: string
  private getAuthToken?: () => Promise<string>
  private getKeyterms?: () => string[]
  private ws: WebSocket | null = null
  private finalText = ''
  private partialText = ''
  private detectedLanguage = 'en'
  private languageReported = false
  /** Length-weighted sum of final-segment confidences, and the length it covers. */
  private confidenceSum = 0
  private confidenceWeight = 0
  private partialConfidence: number | undefined
  private onPartial: ((text: string) => void) | null = null
  private resolveStop: ((result: TranscriptionResult) => void) | null = null

  constructor(
    apiBase: string,
    getAuthToken?: () => Promise<string>,
    options?: TranscriptionClientOptions,
  ) {
    // Convert HTTP URL to WebSocket URL
    const base = apiBase.replace(/^http/, 'ws')
    this.wsUrl = `${base}/transcribe-stream`
    this.getAuthToken = getAuthToken
    this.getKeyterms = options?.getKeyterms
  }

  private url(): string {
    let terms: string[] = []
    try {
      terms = this.getKeyterms?.() ?? []
    } catch {
      // Hints are an extra; never block the connection on them.
    }
    if (terms.length === 0) return this.wsUrl
    return `${this.wsUrl}?${terms.map((t) => `keyterm=${encodeURIComponent(t)}`).join('&')}`
  }

  /** Open WebSocket connection. Call before sending audio chunks. */
  async connect(onPartial?: (text: string) => void): Promise<void> {
    this.finalText = ''
    this.partialText = ''
    this.detectedLanguage = 'en'
    this.languageReported = false
    this.confidenceSum = 0
    this.confidenceWeight = 0
    this.partialConfidence = undefined
    this.onPartial = onPartial ?? null

    // Browsers can't set Authorization on a WebSocket, so the Cognito token
    // rides in the Sec-WebSocket-Protocol subprotocol as ['bearer', <jwt>].
    // The server validates it before accepting and echoes back 'bearer'.
    const token = this.getAuthToken ? await this.getAuthToken() : undefined
    const protocols = token ? ['bearer', token] : undefined

    return new Promise<void>((resolve, reject) => {
      this.ws = new WebSocket(this.url(), protocols)

      this.ws.onopen = () => resolve()

      this.ws.onerror = () => {
        reject(new Error('Transcription WebSocket failed to connect'))
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data.language) {
            this.detectedLanguage = data.language
            this.languageReported = true
          }
          if (data.type === 'final') {
            if (typeof data.confidence === 'number') {
              // Weighted by length: a one-word low-confidence chunk must not
              // sink a long, clearly heard question.
              const weight = Math.max(String(data.text ?? '').length, 1)
              this.confidenceSum += data.confidence * weight
              this.confidenceWeight += weight
            }
            this.finalText += (this.finalText ? ' ' : '') + data.text
            this.partialText = ''
            this.onPartial?.(this.finalText)
          } else if (data.type === 'partial') {
            this.partialText = data.text
            this.partialConfidence =
              typeof data.confidence === 'number' ? data.confidence : undefined
            this.onPartial?.(this.finalText + (this.finalText ? ' ' : '') + this.partialText)
          }
        } catch {
          // Ignore malformed messages
        }
      }

      this.ws.onclose = () => {
        // Deliver final transcript + detected language when connection closes
        this.resolveStop?.(this.result())
        this.resolveStop = null
      }
    })
  }

  private result(): TranscriptionResult {
    // Trailing words the provider has not finalized yet still belong to the
    // question: join them instead of dropping them.
    const text = [this.finalText, this.partialText].filter(Boolean).join(' ')
    const result: TranscriptionResult = {
      text,
      language: this.detectedLanguage,
    }
    if (this.languageReported) result.detectedLanguage = this.detectedLanguage
    let confidence: number | undefined
    if (this.confidenceWeight > 0) {
      confidence = this.confidenceSum / this.confidenceWeight
      if (this.partialText && this.partialConfidence !== undefined) {
        const w = this.partialText.length
        confidence = (this.confidenceSum + this.partialConfidence * w) / (this.confidenceWeight + w)
      }
    } else {
      confidence = this.partialConfidence
    }
    if (confidence !== undefined) result.confidence = confidence
    return result
  }

  /** Send an audio chunk (PCM16 ArrayBuffer) */
  sendAudio(chunk: ArrayBuffer): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(chunk)
    }
  }

  /** Signal end of audio and get the final transcript + detected language */
  async stop(): Promise<TranscriptionResult> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return this.result()
    }

    return new Promise<TranscriptionResult>((resolve) => {
      this.resolveStop = resolve

      // Tell server to finalize
      this.ws!.send(JSON.stringify({ type: 'stop' }))

      // Fallback timeout in case the WebSocket doesn't close cleanly
      setTimeout(() => {
        if (this.resolveStop) {
          const result = this.result()
          this.resolveStop = null
          this.ws?.close()
          this.ws = null
          resolve(result)
        }
      }, 2000)
    })
  }

  /** Close without waiting for final transcript */
  disconnect(): void {
    this.resolveStop = null
    this.ws?.close()
    this.ws = null
  }
}
